import { observer }              from 'mobx-react';
import { useEffect }             from 'react';
import { notificationsSidebar }  from '@stonecode/notifications-sidebar';
import Button                    from '@widesk-ui/components/Button';
import Result                    from '@widesk-ui/components/Result';
import Title                     from '@widesk-ui/components/Title';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import View                      from '@widesk-ui/components/View';
import useTheme                  from '@widesk-ui/hooks/useTheme';
import { useAuth }               from '@widesk-ui/hooks/useAuth';
import useApiCollection          from '@widesk-core/hooks/useApiCollection';
import UserAccountModel          from '@/models/services/account/UserAccountModel';
import useAccount                from '@/hooks/useAccount';
import useDesignTokens           from '@/hooks/useDesignTokens';
import appStore                  from '@/stores/appStore';
import { AccountFailure }        from '@/stores/appStore';
import accountFailureMessage     from './accountFailureMessage';

const AccountName = (props: { name: string }) => {
	const theme = useTheme();

	return (
		<Tooltip title={props.name}>
			<span
				style={{
					fontSize: theme.fontSizeHeading5,
					fontWeight: 'bold',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{props.name}
			</span>
		</Tooltip>
	);
};

const AccountFailureScreen = (props: { failure: AccountFailure }) => {
	const { logout } = useAuth();
	const { selectAccount } = useAccount();
	const isRefused = props.failure.reason === 'refused';

	return (
		<View heightF center padding="lg">
			<Result
				status={isRefused ? 'error' : 'warning'}
				title={isRefused ? 'Accès refusé' : 'Compte indisponible'}
				subTitle={accountFailureMessage(props.failure)}
				extra={(
					<View row center gap="sm">
						<Button type="primary" onClick={() => selectAccount(props.failure.accountId)}>Réessayer</Button>
						<Button type="link" onClick={() => logout()}>Se déconnecter</Button>
					</View>
				)}
			/>
		</View>
	);
};

const AccountCard = (props: { children: React.ReactNode; action: React.ReactNode }) => {
	const tokens = useDesignTokens();

	return (
		<View
			row
			space="between"
			center
			bg={tokens.surface.card}
			padding="lg"
			br={8}
			style={{ boxShadow: tokens.shadow.card }}
			gap="sm"
		>
			<View flex gap="xxs" style={{ minWidth: 0 }}>
				{props.children}
			</View>
			<View marginL="lg" style={{ flexShrink: 0 }}>
				{props.action}
			</View>
		</View>
	);
};

const AccountWall = observer(() => {
	const tokens = useDesignTokens();
	const userAccountCollection = useApiCollection(UserAccountModel);
	const { selectAccount } = useAccount();
	const failure = appStore.accountFailure;

	useEffect(() => {
		(async () => {
			if (!appStore.user.urn) return;

			await userAccountCollection.listBy(appStore.user.urn, 'user');

			const failedAccountId = appStore.accountFailure?.accountId;

			if (userAccountCollection.length === 1) {
				const accountId = userAccountCollection.first()!.getId('account');

				if (accountId !== failedAccountId) selectAccount(accountId);
			} else {
				await userAccountCollection.whenIsLoaded(m => m.getId('account') === failedAccountId
					? []
					: [m.account, m.account.accountStatus]);
			}

			notificationsSidebar.watchTrigger();
		})();
	}, [appStore.user.urn]);

	const onlyAccountId = userAccountCollection.length === 1 ? userAccountCollection.first()!.getId('account') : undefined;

	if (failure && failure.accountId === onlyAccountId) return <AccountFailureScreen failure={failure} />;

	return (
		<View heightF center padding="lg">
			<View gap="sm" center style={{ width: '100%', maxWidth: 420 }}>
				<Title level={2} bold align="center">
					{appStore.user.firstname ? `Bonjour ${appStore.user.firstname}` : 'Bonjour'}
				</Title>
				<Title level={5} color={tokens.text.secondary} marginB="lg">Sélectionnez un compte</Title>
				<View className="account-wall__container" widthF gap="sm">
					{userAccountCollection.map(userAccount => {
						const accountId = userAccount.getId('account');

						if (failure && accountId === failure.accountId) {
							return (
								<AccountCard
									key={userAccount.id}
									action={<Button onClick={() => selectAccount(accountId)}>Réessayer</Button>}
								>
									<Title marginB={0} level={5} bold>Compte inaccessible</Title>
									<View size="small" color={tokens.text.muted}>{accountFailureMessage(failure)}</View>
								</AccountCard>
							);
						}

						const account = userAccount.account;

						return (
							<AccountCard
								key={userAccount.id}
								action={<Button onClick={() => selectAccount(accountId)} type="primary">Sélectionner</Button>}
							>
								{account.name
									? <AccountName name={account.name} />
									: <Title marginB={0} level={5} bold>{account.renderName()}</Title>}
								{account.accountStatus.renderName()}
							</AccountCard>
						);
					})}
				</View>
			</View>
		</View>
	);
});

export default AccountWall;
